import { useEffect, useRef, useState } from "react";
import { FileDown, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { Area, AreaChart, Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { api, errMsg } from "@/lib/api";
import { fmtInt } from "@/lib/format";
import { CustomerPicker } from "@/components/super/CustomerPicker";

const short = (d) => `${d.slice(8, 10)}.${d.slice(5, 7)}`;

const toPng = (src) =>
  new Promise((resolve, reject) => {
    const img = new Image();
    img.onload = () => {
      const cv = document.createElement("canvas");
      cv.width = img.naturalWidth;
      cv.height = img.naturalHeight;
      cv.getContext("2d").drawImage(img, 0, 0);
      resolve({ url: cv.toDataURL("image/png"), w: img.naturalWidth, h: img.naturalHeight });
    };
    img.onerror = reject;
    img.src = src;
  });

async function exportPdf(data, days, chartEl) {
  const [{ jsPDF }, { default: html2canvas }] = await Promise.all([import("jspdf"), import("html2canvas")]);
  const doc = new jsPDF({ unit: "mm", format: "a4", compress: true });
  const name = data.customer?.name || "Alle kunder";
  let y = 16;
  if (data.customer?.logo) {
    const logo = await toPng(data.customer.logo);
    const h = Math.min(18, (40 * logo.h) / logo.w);
    doc.addImage(logo.url, "PNG", 15, y, (h * logo.w) / logo.h, h);
    y += h + 8;
  }
  doc.setFont("helvetica", "bold").setFontSize(18).text(`Statistikk - ${name}`, 15, y);
  y += 8;
  const s = data.series;
  doc.setFont("helvetica", "normal").setFontSize(10).setTextColor(90);
  doc.text(`Periode: siste ${days} dager (${s[0].date} til ${s[s.length - 1].date})`, 15, y);
  y += 6;
  doc.setTextColor(20).text(`Visninger: ${fmtInt(data.totals.views)}    Beregninger: ${fmtInt(data.totals.calcs)}`, 15, y);
  y += 6;
  const canvas = await html2canvas(chartEl, { scale: 2, backgroundColor: "#ffffff" });
  const ih = (180 * canvas.height) / canvas.width;
  doc.addImage(canvas.toDataURL("image/jpeg", 0.85), "JPEG", 15, y, 180, ih, undefined, "FAST");
  y += ih + 8;
  const rows = data.customer ? s.map((r) => [r.date, r.views, r.calcs]) : data.per_customer.map((r) => [r.name, r.views, r.calcs]);
  doc.setFont("helvetica", "bold").text(data.customer ? "Dato" : "Kunde", 15, y).text("Visninger", 130, y).text("Beregninger", 165, y);
  doc.setFont("helvetica", "normal");
  rows.forEach((r) => {
    y += 6;
    if (y > 285) {
      doc.addPage();
      y = 16;
    }
    doc.text(String(r[0]).slice(0, 60), 15, y).text(fmtInt(r[1]), 130, y).text(fmtInt(r[2]), 165, y);
  });
  doc.setFontSize(8).setTextColor(130).text(`Generert ${new Date().toLocaleString("nb-NO")} - Havnekalkulator`, 15, 292);
  doc.save(`statistikk-${name.toLowerCase().replace(/[^a-z0-9æøå]+/g, "-")}-${s[s.length - 1].date}.pdf`);
}

export const StatsPanel = ({ customers, selected, setSelected }) => {
  const [days, setDays] = useState("30");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const chartRef = useRef(null);

  useEffect(() => {
    api.get("/admin/stats", { params: { days, customer_id: selected === "all" ? undefined : selected } })
      .then((r) => setData(r.data))
      .catch((e) => toast.error(errMsg(e)));
  }, [days, selected]);

  const onExport = async () => {
    setBusy(true);
    try {
      await exportPdf(data, days, chartRef.current);
    } catch (e) {
      toast.error("Kunne ikke lage PDF");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 sm:p-6" data-testid="stats-panel">
      <div className="flex flex-col md:flex-row md:items-center gap-3 justify-between mb-6">
        <div className="flex flex-wrap items-end gap-2">
          <div>
            <div className="text-xs font-medium text-slate-500 mb-1">Kunde</div>
            <CustomerPicker customers={customers} value={selected} onChange={setSelected} />
          </div>
          <Select value={days} onValueChange={setDays}>
            <SelectTrigger className="w-40" data-testid="stats-days-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="7">Siste 7 dager</SelectItem>
              <SelectItem value="30">Siste 30 dager</SelectItem>
              <SelectItem value="90">Siste 90 dager</SelectItem>
              <SelectItem value="365">Siste 12 måneder</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button variant="outline" onClick={onExport} disabled={!data || busy} data-testid="stats-export-pdf">
          {busy ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <FileDown className="w-4 h-4 mr-2" />}Eksporter PDF
        </Button>
      </div>
      <div ref={chartRef} className="bg-white">
        <div className="flex gap-8 mb-4">
          <div><div className="text-xs text-slate-500">Visninger</div><div className="font-display text-2xl font-semibold font-num" data-testid="stats-total-views">{fmtInt(data?.totals.views)}</div></div>
          <div><div className="text-xs text-slate-500">Beregninger</div><div className="font-display text-2xl font-semibold font-num" data-testid="stats-total-calcs">{fmtInt(data?.totals.calcs)}</div></div>
        </div>
        <div className="h-64">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={data?.series || []} margin={{ left: -20, right: 8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis dataKey="date" tickFormatter={short} tick={{ fontSize: 11 }} minTickGap={20} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip labelFormatter={short} />
              <Legend />
              <Area type="monotone" dataKey="views" name="Visninger" stroke="#0284c7" fill="#0284c7" fillOpacity={0.15} strokeWidth={2} isAnimationActive={false} />
              <Area type="monotone" dataKey="calcs" name="Beregninger" stroke="#10b981" fill="#10b981" fillOpacity={0.15} strokeWidth={2} isAnimationActive={false} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
        {selected === "all" && !!data?.per_customer.length && (
          <div className="h-64 mt-8">
            <div className="text-sm font-medium text-slate-700 mb-2">Bruk per kunde</div>
            <ResponsiveContainer width="100%" height="90%">
              <BarChart data={data.per_customer.slice(0, 15)} margin={{ left: -20, right: 8 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="views" name="Visninger" fill="#0284c7" isAnimationActive={false} />
                <Bar dataKey="calcs" name="Beregninger" fill="#10b981" isAnimationActive={false} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    </div>
  );
};
